import { AxiosError } from 'axios'
import { useState } from 'react'

// Mutation
interface UseApiMutationOptions<TResponse> {
  onSuccess?: (response: TResponse) => void | Promise<void>
  onError?: (error: any) => void | Promise<void>
}

// Mutation without args
export function useApiMutation<TResponse>(
  mutationFn: () => Promise<TResponse>,
  options?: UseApiMutationOptions<TResponse>
): {
  data: TResponse | undefined
  error: any
  loading: boolean
  mutate: () => Promise<TResponse>
}

// Mutation with args
export function useApiMutation<TResponse, TArgs>(
  mutationFn: (args: TArgs) => Promise<TResponse>,
  options?: UseApiMutationOptions<TResponse>
): {
  data: TResponse | undefined
  error: any
  loading: boolean
  mutate: (args: TArgs) => Promise<TResponse>
}

export function useApiMutation<TResponse, TArgs>(
  mutationFn:
    | ((args: TArgs) => Promise<TResponse>)
    | (() => Promise<TResponse>),
  options: UseApiMutationOptions<TResponse> = {}
) {
  const [data, setData] = useState<TResponse>()
  const [error, setError] = useState<any>(undefined)
  const [loading, setLoading] = useState<boolean>(false)

  const mutate = async (args?: TArgs) => {
    setLoading(true)
    setError(undefined)
    try {
      const response = await (mutationFn as any)(args)
      setData(response)
      await options.onSuccess?.(response)
      return response
    } catch (err) {
      if (err instanceof AxiosError) {
        setError(err.response?.data)
        await options.onError?.(err.response?.data)
      } else {
        setError(err)
        await options.onError?.(err)
      }
    } finally {
      setLoading(false)
    }
  }

  return { data, error, loading, mutate }
}
